/**
 * Mock implementation for Feature Request Requester API.
 * Extends FeatureRequestRequesterClient, overriding fetchByRequester() and
 * fetchDetail() with in-memory data filtered to a single requester.
 *
 * To disable: revert the factory change in packages/app/src/apis.ts
 */
import {
  FeatureRequest,
  FeatureRequestAttachment,
  FeatureRequestListItem,
  FeatureRequestPriority,
  FeatureRequestStatus,
  PaginatedResponse,
  RequesterFeatureRequestFilters,
} from '@agilelab/plugin-wb-builder-common';
import { FeatureRequestRequesterClient } from '../apis/FeatureRequestRequesterApi';

const MOCK_ATTACHMENTS: FeatureRequestAttachment[] = [
  {
    id: 'att-r01',
    filename: 'api-spec.png',
    contentType: 'image/png',
    size: 18200,
  },
  {
    id: 'att-r02',
    filename: 'use-case.txt',
    contentType: 'text/plain',
    size: 850,
  },
];

const now = new Date();
const daysAgo = (d: number) =>
  new Date(now.getTime() - d * 86400000).toISOString();

// All requests belong to the "current user" (by-requester endpoint filters server-side)
const MOCK_MY_REQUESTS: FeatureRequest[] = [
  {
    id: 'my-fr-001',
    summary: 'Add real-time data synchronization',
    description:
      'We need real-time sync between the source database and the data product output port. Currently there is a 15-minute lag.',
    status: FeatureRequestStatus.SUBMITTED,
    priority: FeatureRequestPriority.HIGH,
    projectUrn: 'urn:dmb:sys:finance-data-product',
    componentUrn: 'urn:dmb:cmp:finance-data-product:output-port-postgres',
    requesterRef: 'user:default/guest',
    attachments: [MOCK_ATTACHMENTS[0]],
    createdAt: daysAgo(2),
    updatedAt: daysAgo(1),
  },
  {
    id: 'my-fr-002',
    summary: 'Support Avro schema evolution',
    description:
      'The output port should support backward-compatible Avro schema evolution so consumers do not break.',
    status: FeatureRequestStatus.APPROVED,
    priority: FeatureRequestPriority.MEDIUM,
    projectUrn: 'urn:dmb:sys:finance-data-product',
    componentUrn: 'urn:dmb:cmp:finance-data-product:output-port-kafka',
    requesterRef: 'user:default/guest',
    attachments: [MOCK_ATTACHMENTS[1]],
    createdAt: daysAgo(5),
    updatedAt: daysAgo(3),
  },
  {
    id: 'my-fr-003',
    summary: 'Add column-level data masking',
    description:
      'PII columns (email, phone) must be masked for non-privileged consumers. Need configurable masking policies per column.',
    status: FeatureRequestStatus.IN_PROGRESS,
    priority: FeatureRequestPriority.CRITICAL,
    projectUrn: 'urn:dmb:sys:hr-data-product',
    componentUrn: null,
    requesterRef: 'user:default/guest',
    attachments: [],
    createdAt: daysAgo(10),
    updatedAt: daysAgo(1),
  },
  {
    id: 'my-fr-004',
    summary: 'Expose data quality metrics via API',
    description:
      'Want to programmatically check data freshness and completeness scores before triggering pipelines.',
    status: FeatureRequestStatus.IMPLEMENTED,
    priority: FeatureRequestPriority.LOW,
    projectUrn: 'urn:dmb:sys:hr-data-product',
    componentUrn: 'urn:dmb:cmp:hr-data-product:output-port-postgres',
    requesterRef: 'user:default/guest',
    attachments: [],
    createdAt: daysAgo(30),
    updatedAt: daysAgo(7),
  },
  {
    id: 'my-fr-005',
    summary: 'Add webhook notifications for data refresh',
    description:
      'We need webhook callbacks when the data product completes a refresh cycle so consumers can trigger downstream ETL.',
    status: FeatureRequestStatus.REJECTED,
    priority: FeatureRequestPriority.MEDIUM,
    projectUrn: 'urn:dmb:sys:marketing-data-product',
    componentUrn: null,
    requesterRef: 'user:default/guest',
    attachments: [MOCK_ATTACHMENTS[0], MOCK_ATTACHMENTS[1]],
    createdAt: daysAgo(15),
    updatedAt: daysAgo(12),
  },
  {
    id: 'my-fr-006',
    summary: 'Support incremental loads for large tables',
    description:
      'Full table scans take >2 hours. Need CDC-based incremental loads to reduce latency under 5 minutes.',
    status: FeatureRequestStatus.SUBMITTED,
    priority: FeatureRequestPriority.HIGH,
    projectUrn: 'urn:dmb:sys:marketing-data-product',
    componentUrn: 'urn:dmb:cmp:marketing-data-product:output-port-s3',
    requesterRef: 'user:default/guest',
    attachments: [],
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  {
    id: 'my-fr-007',
    summary: 'Add SLA monitoring dashboard',
    description:
      'Need a dashboard that tracks SLA compliance for data delivery commitments across all output ports.',
    status: FeatureRequestStatus.SUBMITTED,
    priority: FeatureRequestPriority.MEDIUM,
    projectUrn: 'urn:dmb:sys:finance-data-product',
    componentUrn: null,
    requesterRef: 'user:default/guest',
    attachments: [],
    createdAt: daysAgo(3),
    updatedAt: daysAgo(3),
  },
  {
    id: 'my-fr-008',
    summary: 'Enable cross-region replication',
    description:
      'For disaster recovery, need the output port data replicated to a secondary region with <1h RPO.',
    status: FeatureRequestStatus.APPROVED,
    priority: FeatureRequestPriority.CRITICAL,
    projectUrn: 'urn:dmb:sys:hr-data-product',
    componentUrn: 'urn:dmb:cmp:hr-data-product:output-port-postgres',
    requesterRef: 'user:default/guest',
    attachments: [],
    createdAt: daysAgo(8),
    updatedAt: daysAgo(4),
  },
  {
    id: 'my-fr-009',
    summary: 'Add data versioning support',
    description:
      'Need to query historical snapshots of data at specific points in time for audit and reproducibility.',
    status: FeatureRequestStatus.IN_PROGRESS,
    priority: FeatureRequestPriority.HIGH,
    projectUrn: 'urn:dmb:sys:marketing-data-product',
    componentUrn: null,
    requesterRef: 'user:default/guest',
    attachments: [],
    createdAt: daysAgo(20),
    updatedAt: daysAgo(2),
  },
  {
    id: 'my-fr-010',
    summary: 'Improve error messages for access denied',
    description:
      'Current 403 error is generic. Need descriptive messages explaining which policy blocked access and how to request it.',
    status: FeatureRequestStatus.SUBMITTED,
    priority: FeatureRequestPriority.LOW,
    projectUrn: 'urn:dmb:sys:finance-data-product',
    componentUrn: null,
    requesterRef: 'user:default/guest',
    attachments: [],
    createdAt: daysAgo(4),
    updatedAt: daysAgo(4),
  },
  {
    id: 'my-fr-011',
    summary: 'Enable GDPR data deletion requests',
    description:
      'Support right-to-erasure requests. All PII for that subject must be removed within 72 hours.',
    status: FeatureRequestStatus.IN_PROGRESS,
    priority: FeatureRequestPriority.CRITICAL,
    projectUrn: 'urn:dmb:sys:hr-data-product',
    componentUrn: null,
    requesterRef: 'user:default/guest',
    attachments: [],
    createdAt: daysAgo(18),
    updatedAt: daysAgo(1),
  },
  {
    id: 'my-fr-012',
    summary: 'Add data lineage tracking',
    description:
      'Need to trace data lineage from source to output port to understand transformations and verify data provenance.',
    status: FeatureRequestStatus.SUBMITTED,
    priority: FeatureRequestPriority.HIGH,
    projectUrn: 'urn:dmb:sys:marketing-data-product',
    componentUrn: 'urn:dmb:cmp:marketing-data-product:output-port-s3',
    requesterRef: 'user:default/guest',
    attachments: [],
    createdAt: daysAgo(9),
    updatedAt: daysAgo(9),
  },
];

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export class MockFeatureRequestRequesterClient extends FeatureRequestRequesterClient {
  private mockData: FeatureRequest[] = [...MOCK_MY_REQUESTS];

  async fetchByRequester(
    filters: RequesterFeatureRequestFilters,
  ): Promise<PaginatedResponse<FeatureRequestListItem>> {
    await delay(400);

    let filtered = [...this.mockData];

    if (filters.projectUrn) {
      filtered = filtered.filter(r => r.projectUrn === filters.projectUrn);
    }
    if (filters.status) {
      filtered = filtered.filter(r => r.status === filters.status);
    }
    if (filters.priority) {
      filtered = filtered.filter(r => r.priority === filters.priority);
    }

    filtered.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    const offset = filters.offset ?? 0;
    const limit = filters.limit ?? 15;
    const paged = filtered.slice(offset, offset + limit);

    const items: FeatureRequestListItem[] = paged.map(r => ({
      id: r.id,
      summary: r.summary,
      status: r.status,
      priority: r.priority,
      requesterRef: r.requesterRef,
      componentUrn: r.componentUrn,
      projectUrn: r.projectUrn,
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
    }));

    return { items, total: filtered.length, offset, limit };
  }

  async fetchDetail(id: string): Promise<FeatureRequest> {
    await delay(300);
    const found = this.mockData.find(r => r.id === id);
    if (!found) {
      throw new Error(`Feature request ${id} not found`);
    }
    return { ...found };
  }
}
